import { useEffect, useState } from "react";
import { ACCESS_KEY } from "../config";
import "./Applications.css";

const API_BASE = "https://admin.butterlive.co.in";

function Applications() {
  const [applications, setApplications] = useState([]);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewCode, setReviewCode] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");

  const handleApprove = async () => {
    if (!selectedApplication) return;

    const codeNo = window.prompt("Enter Code No for approval:");
    if (codeNo === null) return;

    if (!codeNo.trim()) {
      alert("Code No is required.");
      return;
    }

    try {
      setReviewLoading(true);

      const response = await fetch(
        `${API_BASE}/management/application/review`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            key: ACCESS_KEY,
          },
          body: JSON.stringify({
            applicationId: selectedApplication._id,
            action: "approve",
            codeNo: codeNo.trim(),
          }),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.status) {
        throw new Error(result.message || "Failed to approve application.");
      }

      alert(result.message || "Application approved successfully.");
      setSelectedApplication(null);
      setReviewCode("");
      await fetchApplications();
    } catch (error) {
      console.error("Approve Application Error:", error);
      alert(error.message || "Unable to approve application.");
    } finally {
      setReviewLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedApplication) return;

    const reason = window.prompt("Enter rejection reason:");
    if (reason === null) return;

    if (!reason.trim()) {
      alert("Rejection reason is required.");
      return;
    }

    try {
      setReviewLoading(true);

      const response = await fetch(
        `${API_BASE}/management/application/review`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            key: ACCESS_KEY,
          },
          body: JSON.stringify({
            applicationId: selectedApplication._id,
            action: "reject",
            rejectionReason: reason.trim(),
          }),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.status) {
        throw new Error(result.message || "Failed to reject application.");
      }

      alert(result.message || "Application rejected successfully.");
      setSelectedApplication(null);
      setRejectionReason("");
      await fetchApplications();
    } catch (error) {
      console.error("Reject Application Error:", error);
      alert(error.message || "Unable to reject application.");
    } finally {
      setReviewLoading(false);
    }
  };

  const fetchApplications = async () => {
    try {
      setLoading(true);

      const response = await fetch(
        `${API_BASE}/management/application/all`,
        {
          headers: {
            key: ACCESS_KEY,
          },
        }
      );

      const result = await response.json();

      if (!response.ok || !result.status) {
        throw new Error(result.message || "Failed to load applications.");
      }

      setApplications(result.data || []);
    } catch (error) {
      console.error("Applications Error:", error);
      alert(error.message || "Unable to load applications.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const filteredApplications = applications.filter(
    (application) => application.status === status
  );

  return (
    <div className="applications-page">
      <div className="applications-header">
        <h1>Applications</h1>
        <p>Manage BD and Agency applications</p>
      </div>

      <div className="application-tabs">
        {["pending", "approved", "rejected"].map((item) => (
          <button
            key={item}
            className={status === item ? "active" : ""}
            onClick={() => setStatus(item)}
          >
            {item.charAt(0).toUpperCase() + item.slice(1)}
          </button>
        ))}
      </div>

      <div className="applications-card">
        {loading ? (
          <div className="applications-empty">
            Loading applications...
          </div>
        ) : filteredApplications.length === 0 ? (
          <div className="applications-empty">
            No {status} applications found.
          </div>
        ) : (
          <div className="applications-table-wrap">
            <table className="applications-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>User</th>
                  <th>Unique ID</th>
                  <th>Parent</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {filteredApplications.map((application) => (
                  <tr key={application._id}>
                    <td>
                      {application.applicationType === "bd"
                        ? "BD Request"
                        : "Agency Request"}
                    </td>

                    <td>
                      {application.applicantUser?.name ||
                        application.name ||
                        "N/A"}
                    </td>

                    <td>{application.uniqueId}</td>

                    <td>
                      {application.parentBD
                        ? `BD ${application.parentBD.codeNo || application.parentBD.uniqueId}`
                        : application.parentAdmin
                        ? `Admin ${application.parentAdmin.codeNo || application.parentAdmin.uniqueId}`
                        : "N/A"}
                    </td>

                    <td>
                      <span className={`application-status ${application.status}`}>
                        {application.status}
                      </span>
                    </td>

                    <td>
                      <button
                        className="application-view-btn"
                        onClick={() => setSelectedApplication(application)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedApplication && (
        <div className="application-modal-overlay">
          <div className="application-modal">
            <div className="application-modal-header">
              <h2>Application Details</h2>

              <button
                className="application-close-btn"
                onClick={() => setSelectedApplication(null)}
              >
                ×
              </button>
            </div>

            <div className="application-details">
              <p>
                <strong>Type:</strong>{" "}
                {selectedApplication.applicationType === "bd"
                  ? "BD Request"
                  : "Agency Request"}
              </p>

              <p>
                <strong>Name:</strong>{" "}
                {selectedApplication.applicantUser?.name ||
                  selectedApplication.name ||
                  "N/A"}
              </p>

              <p>
                <strong>Unique ID:</strong>{" "}
                {selectedApplication.uniqueId}
              </p>

              <p>
                <strong>Phone:</strong>{" "}
                {selectedApplication.phone || "N/A"}
              </p>

              <p>
                <strong>Email:</strong>{" "}
                {selectedApplication.email || "N/A"}
              </p>

              <p>
                <strong>Bank Details:</strong>{" "}
                {selectedApplication.bankDetails || "N/A"}
              </p>

              <p>
                <strong>Documents:</strong>{" "}
                {selectedApplication.documents?.length
                  ? selectedApplication.documents.join(", ")
                  : "N/A"}
              </p>

              <p>
                <strong>Status:</strong>{" "}
                {selectedApplication.status}
              </p>

              {selectedApplication.assignedCode && (
                <p>
                  <strong>Assigned Code:</strong>{" "}
                  {selectedApplication.assignedCode}
                </p>
              )}

              {selectedApplication.rejectionReason && (
                <p>
                  <strong>Rejection Reason:</strong>{" "}
                  {selectedApplication.rejectionReason}
                </p>
              )}
            </div>

            {selectedApplication.status === "pending" && (
              <div className="application-actions">
                <button
                  className="approve-btn"
                  onClick={handleApprove}
                  disabled={reviewLoading}
                >
                  {reviewLoading ? "Processing..." : "Approve"}
                </button>

                <button
                  className="reject-btn"
                  onClick={handleReject}
                  disabled={reviewLoading}
                >
                  {reviewLoading ? "Processing..." : "Reject"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Applications;
